import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  DialogContent,
  DialogTitle,
  Alert,
  Checkbox,
  Chip,
  Tooltip,
  Paper
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SettingIcon from "../../assets/icons/settings.png";
import CustomTableWithPagination from "../../components/customTable/CustomTable";
import styled from "styled-components/macro";
import { constants } from "../../utils/constants";
import Modal from "../../components/modal/ModalPage";
import { getFormattedDate } from "../../utils/commonFunctions";
import CopyToClipboardWrapper from "../../components/copyToClipboardWrapper/CopyToClipboardWrapper";
import { Businesssearch } from "@rewardle/apps.ui.businesssearch.businesssearch";
import CopyableText from "../../components/copyableText/CopyableText";
import CustomButton from "../../components/CustomButton/Button";
import { GET_BUSINESS_SETTINGS } from "../../utils/url";
import ChildBusinessCountCard from "./ChildBusinessCountCard";

const apiEnpoint: any = process.env.REACT_APP_API_BASE_URL;

// Styled components
const BoxModal = styled(Box)`
  display: flex;
  gap: 10px;
`;

const StatusContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
`;

interface ChildBusiness {
  BusinessGroupId: number;
  MerchantId: number;
  BusinessId: string;
  BusinessName: string;
  GroupingDate: string;
}

interface BusinessGroup {
  MerchantId: number;
  BusinessId: string;
  BusinessName: string;
  ChildMerchants: ChildBusiness[];
}

interface BusinessGroupPageProps {}

const BusinessGroupPage: React.FC<BusinessGroupPageProps> = () => {
  const { businessId } = useParams<any>();
  const [businessGroup, setBusinessGroup] = useState<BusinessGroup | any>(null);
  console.log("🚀 ~ businessGroup:", businessGroup);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  // selectedMerchant is used for the delete flow (single)
  const [selectedMerchant, setSelectedMerchant] =
    useState<ChildBusiness | null>(null);
  // selectedBusiness is used for the add flow (multi-select)
  const [selectedBusiness, setSelectedBusiness] = useState<any[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [payloadSize, setPayloadSize] = useState(10);
  const [apiStatus, setApiStatus] = useState<"idle" | "fetching" | "failed">(
    "idle"
  );
  const [errorsMessage, setErrorsMessage] = useState({
    type: "",
    message: ""
  });
  const [parentLoginUserId, setParentLoginUserId] = useState<string | null>(
    null
  );
  const [childBusinessSettings, setChildBusinessSettings] = useState<
    Record<string, { sharePoints: boolean; shareCredits: boolean } | null>
  >({});
  const [businessMessages, setBusinessMessages] = useState<
    Array<{
      businessName: string;
      type: "success" | "error" | "warning";
      message: string;
    }>
  >([]);

  const addDialogContentRef = useRef<HTMLDivElement | null>(null);
  const deleteDialogContentRef = useRef<HTMLDivElement | null>(null);

  const scrollToErrorsMessage = () => {
    const behavior: ScrollBehavior = "smooth";
    if (openAddDialog && addDialogContentRef.current) {
      addDialogContentRef.current.scrollTo({ top: 0, behavior });
      return;
    }
    if (openDeleteModal && deleteDialogContentRef.current) {
      deleteDialogContentRef.current.scrollTo({ top: 0, behavior });
      return;
    }
    window?.scrollTo?.({ top: 0, behavior });
  };

  const accessToken: any = localStorage?.getItem("accessToken");

  useEffect(() => {
    fetchBusinessGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const fetchBusinessGroup = async () => {
    if (!businessId) return;
    setApiStatus("fetching");
    try {
      const [businessGroupResponse, loginDetailsResponse] = await Promise.all([
        axios.get(
          `${apiEnpoint}/billingplatformapi/businessgroup/${businessId}`,
          {
            headers: {
              Authorization: `bearer ${localStorage.getItem("accessToken")}`,
              "Content-Type": "application/json"
            }
          }
        ),
        axios.get(
          `${apiEnpoint}/datacollectionbusiness/support/login/details/${businessId}`,
          {
            headers: {
              Authorization: `bearer ${localStorage.getItem("accessToken")}`,
              "Content-Type": "application/json"
            }
          }
        )
      ]);
      setBusinessGroup(businessGroupResponse.data);
      setParentLoginUserId(loginDetailsResponse.data?.UserId || null);

      // Fetch sharing settings for each child business
      const childMerchants = businessGroupResponse.data?.ChildMerchants || [];
      const settingsPromises = childMerchants.map((child: ChildBusiness) =>
        axios
          .get(
            `${apiEnpoint}${GET_BUSINESS_SETTINGS(
              child.BusinessId,
              "sharing"
            )}`,
            {
              headers: {
                Authorization: `bearer ${localStorage.getItem("accessToken")}`,
                "Content-Type": "application/json"
              }
            }
          )
          .then((response) => {
            if (response?.data && Array.isArray(response.data)) {
              const sharePointsSetting = response.data.find(
                (s: any) => s.SettingName === "SHAREPOINTS"
              );
              const shareCreditsSetting = response.data.find(
                (s: any) => s.SettingName === "SHARECREDITS"
              );
              const spValue =
                (sharePointsSetting?.Merchant_Overwrite_Value ??
                  sharePointsSetting?.DefaultValue) === "true";
              const scValue =
                (shareCreditsSetting?.Merchant_Overwrite_Value ??
                  shareCreditsSetting?.DefaultValue) === "true";
              return {
                businessId: child.BusinessId,
                settings: { sharePoints: spValue, shareCredits: scValue }
              };
            }
            return {
              businessId: child.BusinessId,
              settings: null
            };
          })
          .catch((err) => {
            console.error(
              `Error fetching sharing settings for ${child.BusinessId}:`,
              err
            );
            return {
              businessId: child.BusinessId,
              settings: null
            };
          })
      );

      type SettingsResult = {
        businessId: string;
        settings: { sharePoints: boolean; shareCredits: boolean } | null;
      };
      const settingsResults: SettingsResult[] = await Promise.all(
        settingsPromises
      );
      const settingsMap: Record<
        string,
        { sharePoints: boolean; shareCredits: boolean } | null
      > = {};
      settingsResults.forEach((result) => {
        settingsMap[result.businessId] = result.settings;
      });
      setChildBusinessSettings(settingsMap);

      setApiStatus("idle");
    } catch (error) {
      console.error("Error fetching business group:", error);
      setApiStatus("failed");
    }
  };

  const handleRefreshData = async () => {
    try {
      await Promise.all([fetchBusinessGroup()]);
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  const handleRemoveBusiness = async (business: ChildBusiness) => {
    setSelectedMerchant(business);
    setErrorsMessage({
      type: "",
      message: ""
    });
    setOpenDeleteModal(true);
  };

  const confirmRemoveBusiness = () => {
    if (!selectedMerchant) return;

    setModalLoading(true);
    setErrorsMessage({
      type: "",
      message: ""
    });

    // Step 1: Call remove business API
    return axios
      .delete(`${apiEnpoint}/billingplatformapi/businessgroup`, {
        headers: {
          Authorization: `bearer ${localStorage.getItem("accessToken")}`,
          "Content-Type": "application/json"
        },
        data: {
          ChildBusinessId: selectedMerchant.BusinessId,
          ParentBusinessId: businessGroup?.BusinessId || businessId
        }
      })
      .then((response) => {
        // Only proceed if business removal was successful (status 200)

        // Step 2: Remove role after successful business deletion
        if (parentLoginUserId) {
          return axios
            .delete(`${apiEnpoint}/users/support/user/role`, {
              headers: {
                Authorization: `bearer ${localStorage.getItem("accessToken")}`,
                "Content-Type": "application/json"
              },
              data: {
                UserId: parentLoginUserId,
                SpecificBusinessRoleToBeRemove: `${selectedMerchant.BusinessId}|BusinessAdmin`,
                LegacyUserId: selectedMerchant.MerchantId
              }
            })
            .then(() => {
              // Role removal succeeded
              return { response, roleError: null };
            })
            .catch((roleError) => {
              // Log error but don't fail the entire operation if role removal fails
              console.error("Error removing role:", roleError);
              // Return response with role error attached
              return { response, roleError };
            });
        }
        return { response, roleError: null };
      })
      .then(({ response, roleError }) => {
        // Step 3: Show appropriate message based on results
        if (roleError) {
          // Business removed but role removal failed - show warning
          const getErrorMessage = (error: any, operation: string): string => {
            // Handle error structure: {status: false, err: {...}}
            if (error.response?.data) {
              const responseData = error.response.data;

              // Check for custom error structure
              if (responseData.err) {
                return (
                  responseData.err.message ||
                  responseData.err.code ||
                  `Error occurred while ${operation}.`
                );
              }

              // Handle standard error structure
              if (responseData.message) {
                return responseData.message;
              }

              if (responseData.errors && Array.isArray(responseData.errors)) {
                const errorMessages = responseData.errors
                  .map((item: any) =>
                    typeof item === "string" ? item : item.message || item
                  )
                  .filter(Boolean);
                if (errorMessages.length > 0) {
                  return errorMessages.join(" ");
                }
              }
            }

            const status = error.response?.status;
            if (status === 500) {
              return `Server error occurred while ${operation}. The business was removed successfully, but role removal failed. Please try removing the role manually or contact support.`;
            }

            return `Error occurred while ${operation}. The business was removed successfully, but role removal failed.`;
          };

          const roleErrorMessage = getErrorMessage(
            roleError,
            "removing the business role"
          );
          setErrorsMessage({
            type: "error",
            message: `Business removed successfully, but failed to remove role: ${roleErrorMessage}`
          });
          scrollToErrorsMessage();
          setModalLoading(false);
          // Don't close modal - let user see the error
        } else {
          // Both succeeded
          setErrorsMessage({
            type: "success",
            message:
              response?.data?.toString() ||
              `Successfully removed ${selectedMerchant.BusinessName} from the business group and removed role.`
          });
          setModalLoading(false);
          // Close modal and refresh after showing success message
          setTimeout(() => {
            setOpenDeleteModal(false);
            setSelectedMerchant(null);
            setErrorsMessage({
              type: "",
              message: ""
            });
            fetchBusinessGroup();
          }, 1500);
        }
      })
      .catch((error: any) => {
        // Use the same error handling helper function
        const getErrorMessage = (error: any, operation: string): string => {
          // Network error (no response from server)
          if (!error.response) {
            if (
              error.code === "ECONNABORTED" ||
              error.message?.includes("timeout")
            ) {
              return `Request timed out while ${operation}. Please check your connection and try again.`;
            }
            if (error.message === "Network Error") {
              return `Unable to connect to the server while ${operation}. Please check your internet connection and try again.`;
            }
            return `Network error occurred while ${operation}. Please try again later.`;
          }

          const status = error.response?.status;
          const responseData = error.response?.data;

          // Handle specific HTTP status codes
          switch (status) {
            case 400:
              return (
                responseData?.message ||
                `Invalid request while ${operation}. Please check the information and try again.`
              );
            case 401:
              return `Your session has expired. Please refresh the page and try again.`;
            case 403:
              return `You don't have permission to ${operation}. Please contact your administrator.`;
            case 404:
              return `The requested resource was not found while ${operation}. Please verify the information and try again.`;
            case 500:
              return `Server error occurred while ${operation}. Please try again later or contact support if the problem persists.`;
            case 502:
              return `Service temporarily unavailable while ${operation}. Please try again in a few moments.`;
            case 503:
              return `Service is currently unavailable while ${operation}. Please try again later.`;
            case 504:
              return `Request timed out while ${operation}. Please try again.`;
            default:
              break;
          }

          // Try to extract error message from response
          if (responseData?.errors && Array.isArray(responseData.errors)) {
            const errorMessages = responseData.errors
              .map((item: any) =>
                typeof item === "string" ? item : item.message || item
              )
              .filter(Boolean);
            if (errorMessages.length > 0) {
              return errorMessages.join(" ");
            }
          }

          if (responseData?.message) {
            return responseData.message;
          }

          if (responseData?.error) {
            return typeof responseData.error === "string"
              ? responseData.error
              : responseData.error.message ||
                  `Error occurred while ${operation}.`;
          }

          // Fallback message
          return `An error occurred while ${operation}. Please try again or contact support if the problem persists.`;
        };

        const errorMessage = getErrorMessage(
          error,
          "removing the business from the group"
        );

        setErrorsMessage({
          type: "error",
          message: errorMessage
        });
        scrollToErrorsMessage();
        console.error("Error removing business:", error);
        setModalLoading(false);
      });
  };

  const handleAddBusiness = () => {
    if (!selectedBusiness || selectedBusiness.length === 0) {
      setErrorsMessage({
        type: "error",
        message: "Please select at least one business to add"
      });
      scrollToErrorsMessage();
      return;
    }

    setModalLoading(true);
    setErrorsMessage({
      type: "",
      message: ""
    });
    setBusinessMessages([]);

    // Helper function to get user-friendly error message
    const getErrorMessage = (error: any, operation: string): string => {
      // Network error (no response from server)
      if (!error.response) {
        if (
          error.code === "ECONNABORTED" ||
          error.message?.includes("timeout")
        ) {
          return `Request timed out while ${operation}. Please check your connection and try again.`;
        }
        if (error.message === "Network Error") {
          return `Unable to connect to the server while ${operation}. Please check your internet connection and try again.`;
        }
        return `Network error occurred while ${operation}. Please try again later.`;
      }

      const status = error.response?.status;
      const responseData = error.response?.data;

      // Handle custom error structure: {status: false, err: {...}}
      if (responseData?.err) {
        const errMessage =
          responseData.err.message ||
          responseData.err.code ||
          `Error occurred while ${operation}.`;
        return errMessage;
      }

      // Handle specific HTTP status codes
      switch (status) {
        case 400:
          return (
            responseData?.message ||
            `Invalid request while ${operation}. Please check the information and try again.`
          );
        case 401:
          return `Your session has expired. Please refresh the page and try again.`;
        case 403:
          return `You don't have permission to ${operation}. Please contact your administrator.`;
        case 404:
          return `The requested resource was not found while ${operation}. Please verify the information and try again.`;
        case 500:
          return `Server error occurred while ${operation}. Please try again later or contact support if the problem persists.`;
        case 502:
          return `Service temporarily unavailable while ${operation}. Please try again in a few moments.`;
        case 503:
          return `Service is currently unavailable while ${operation}. Please try again later.`;
        case 504:
          return `Request timed out while ${operation}. Please try again.`;
        default:
          break;
      }

      // Try to extract error message from response
      if (responseData?.errors && Array.isArray(responseData.errors)) {
        const errorMessages = responseData.errors
          .map((item: any) =>
            typeof item === "string" ? item : item.message || item
          )
          .filter(Boolean);
        if (errorMessages.length > 0) {
          return errorMessages.join(" ");
        }
      }

      if (responseData?.message) {
        return responseData.message;
      }

      if (responseData?.error) {
        return typeof responseData.error === "string"
          ? responseData.error
          : responseData.error.message || `Error occurred while ${operation}.`;
      }

      // Fallback message
      return `An error occurred while ${operation}. Please try again or contact support if the problem persists.`;
    };

    const promises = selectedBusiness.map((business) => {
      return axios
        .post(
          `${apiEnpoint}/billingplatformapi/businessgroup/add`,
          {
            ChildBusinessId: business.BusinessId,
            ParentBusinessId: businessGroup?.BusinessId || businessId
          },
          {
            headers: {
              Authorization: `bearer ${localStorage.getItem("accessToken")}`,
              "Content-Type": "application/json"
            }
          }
        )
        .then(() => {
          console.log(
            `Successfully added business to group: ${business.BusinessName}`
          );

          // Try to add role, but don't fail if it doesn't work
          if (parentLoginUserId) {
            return axios
              .post(
                `${apiEnpoint}/users/support/user/role`,
                {
                  UserId: parentLoginUserId,
                  Role: "Business",
                  BusinessRole: "BusinessAdmin",
                  BusinessId: business.BusinessId
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `bearer ${localStorage.getItem(
                      "accessToken"
                    )}`
                  }
                }
              )
              .then(() => ({
                businessName:
                  business.BusinessName || business.BusinessId || "Unknown",
                type: "success" as const,
                message: `${business.BusinessName} successfully added and role assigned`
              }))
              .catch((roleError: any) => {
                console.error("Error assigning role:", roleError);
                const roleErrorMessage = getErrorMessage(
                  roleError,
                  "assigning the role"
                );
                return {
                  businessName:
                    business.BusinessName || business.BusinessId || "Unknown",
                  type: "warning" as const,
                  message: `${business.BusinessName} successfully added, but role assignment failed: ${roleErrorMessage}`
                };
              });
          }

          return {
            businessName:
              business.BusinessName || business.BusinessId || "Unknown",
            type: "success" as const,
            message: `${business.BusinessName} successfully added to the business group`
          };
        })
        .catch((error: any) => {
          const errorMessage = getErrorMessage(
            error,
            "adding the business to the group"
          );
          console.error(
            `Error adding business ${business.BusinessName}:`,
            error
          );
          return {
            businessName:
              business.BusinessName || business.BusinessId || "Unknown",
            type: "error" as const,
            message: errorMessage
          };
        });
    });

    Promise.all(promises)
      .then((allResults) => {
        setBusinessMessages(allResults);

        const allAdded = allResults.every(
          (r) => r.type === "success" || r.type === "warning"
        );
        const hasErrors = allResults.some((r) => r.type === "error");

        if (allAdded) {
          fetchBusinessGroup();
          if (!hasErrors) {
            setTimeout(() => {
              setOpenAddDialog(false);
              setSelectedBusiness([]);
              setBusinessMessages([]);
            }, 1500);
          }
        }
      })
      .catch((error) => {
        console.error("Unexpected error processing businesses:", error);
      })
      .finally(() => {
        setModalLoading(false);
      });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPayloadSize(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const columns = [
    {
      id: "BusinessName",
      label: "Business Name",
      format: (value: string | null, row: ChildBusiness) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {value || "-"}
          {value && (
            <CopyToClipboardWrapper
              text={row?.BusinessId?.toString() || ""}
              cell="Business ID"
            />
          )}
        </Box>
      )
    },
    {
      id: "GroupingDate",
      label: "Grouping Date",
      format: (value: string) => (value ? getFormattedDate(value) : "-")
    },
    {
      id: "SharePoints",
      label: "Share Points",
      format: (value: any, row: ChildBusiness) => {
        const settings = childBusinessSettings[row.BusinessId];
        if (settings?.sharePoints === undefined) {
          return (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          );
        }
        return (
          <StatusContainer>
            {settings.sharePoints ? (
              <>
                <CheckCircleIcon sx={{ color: "#4caf50", fontSize: "20px" }} />
                <Chip
                  label="Enabled"
                  size="small"
                  sx={{
                    backgroundColor: "#e8f5e9",
                    color: "#2e7d32",
                    fontWeight: 500,
                    height: "24px"
                  }}
                />
              </>
            ) : (
              <>
                <CancelIcon sx={{ color: "#f44336", fontSize: "20px" }} />
                <Chip
                  label="Disabled"
                  size="small"
                  sx={{
                    backgroundColor: "#ffebee",
                    color: "#c62828",
                    fontWeight: 500,
                    height: "24px"
                  }}
                />
              </>
            )}
          </StatusContainer>
        );
      }
    },
    {
      id: "ShareCredits",
      label: "Share Credits",
      format: (value: any, row: ChildBusiness) => {
        const settings = childBusinessSettings[row.BusinessId];
        return (
          <>
            {settings?.shareCredits === undefined ? (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            ) : (
              <StatusContainer>
                {settings.shareCredits ? (
                  <>
                    <CheckCircleIcon
                      sx={{ color: "#4caf50", fontSize: "20px" }}
                    />
                    <Chip
                      label="Enabled"
                      size="small"
                      sx={{
                        backgroundColor: "#e8f5e9",
                        color: "#2e7d32",
                        fontWeight: 500,
                        height: "24px"
                      }}
                    />
                  </>
                ) : (
                  <>
                    <CancelIcon sx={{ color: "#f44336", fontSize: "20px" }} />
                    <Chip
                      label="Disabled"
                      size="small"
                      sx={{
                        backgroundColor: "#ffebee",
                        color: "#c62828",
                        fontWeight: 500,
                        height: "24px"
                      }}
                    />
                  </>
                )}
              </StatusContainer>
            )}
          </>
        );
      }
    },
    {
      id: "actions",
      label: "Actions",
      format: (value: any, row: ChildBusiness) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Tooltip title="Change sharing settings">
            <img
              src={SettingIcon}
              alt="Change sharing settings"
              style={{
                cursor: "pointer",
                width: "20px",
                height: "20px"
              }}
              onClick={(e: any) => {
                e.stopPropagation();
                window.open(
                  `/settings/${row.BusinessId}/${row.MerchantId}?tab=3`,
                  "_blank"
                );
              }}
            />
          </Tooltip>
          {/* <DeleteIcon
            sx={{ cursor: "pointer" }}
            onClick={() => handleRemoveBusiness(row)}
          /> */}
        </Box>
      )
    }
  ];

  const searchBusinessColumns: any = [
    {
      field: "checkbox",
      headerName: "",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 50,
      renderCell: (params: any) => (
        <Checkbox
          checked={selectedBusiness.some(
            (b) => b.BusinessId === params.row.BusinessId
          )}
          onChange={() => {
            const isSelected = selectedBusiness.some(
              (b) => b.BusinessId === params.row.BusinessId
            );
            if (isSelected) {
              setSelectedBusiness(
                selectedBusiness.filter(
                  (b) => b.BusinessId !== params.row.BusinessId
                )
              );
            } else {
              setSelectedBusiness([...selectedBusiness, params.row]);
            }
          }}
          sx={{
            color: "#6E759F",
            "&.Mui-checked": {
              color: "#6E759F"
            }
          }}
          aria-label={`Select ${params.row.BusinessName}`}
        />
      )
    },
    {
      field: "BusinessName",
      headerName: "BusinessName",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 400,
      renderCell: (params: any) =>
        params?.row?.BusinessName ? params?.row?.BusinessName : "-"
    },
    {
      field: "BusinessId",
      headerName: "BusinessId",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 140,
      renderCell: (params: any) => (
        <div>
          {params?.row?.BusinessId ? (
            <CopyableText text={params?.row?.BusinessId} />
          ) : (
            "-"
          )}
        </div>
      )
    },
    {
      field: "MerchantId",
      headerName: "MerchantId",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 140,
      renderCell: (params: any) => (
        <div>
          {params?.row?.MerchantId ? (
            <CopyableText text={params?.row?.MerchantId} />
          ) : (
            "-"
          )}
        </div>
      )
    },
    {
      field: "Postcode",
      headerName: "Postcode",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 170,
      renderCell: (params: any) =>
        params?.row?.Address?.PostCode ? params?.row?.Address?.PostCode : "-"
    },
    {
      field: "State",
      headerName: "State",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 170,
      renderCell: (params: any) =>
        params?.row?.Address?.State ? params?.row?.Address?.State : "-"
    },
    {
      field: "AdminEmail",
      headerName: "AdminEmail",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 300,
      renderCell: (params: any) => (
        <div>{params?.row?.AdminEmail ? params?.row?.AdminEmail : "-"}</div>
      )
    },
    {
      field: "LoginEmail",
      headerName: "LoginEmail",
      headerClassName: "customTableHeader",
      cellClassName: "customTableCell",
      width: 300,
      renderCell: (params: any) =>
        params?.row?.LoginEmail ? params?.row?.LoginEmail : "-"
    }
  ];

  const hasValidData =
    businessGroup?.ChildMerchants && businessGroup?.ChildMerchants?.length > 0;

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "10px",
          alignContent: "center",
          justifyContent: "end",
          mb: 2
        }}
      >
        <Button
          color="primary"
          style={{ marginTop: 2 }}
          className="blue_button"
          startIcon={
            <span style={{ display: "flex", alignItems: "center" }}>
              <AddIcon /> Add Child Business
            </span>
          }
          onClick={() => {
            setErrorsMessage({
              type: "",
              message: ""
            });
            setBusinessMessages([]);
            setSelectedBusiness([]);
            setOpenAddDialog(true);
          }}
          disabled={apiStatus === "fetching"}
        ></Button>
        <RefreshIcon onClick={handleRefreshData} sx={{ cursor: "pointer" }} />
      </Box>

      {/* Parent Business Section */}
      {businessGroup && (
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: "#f5f5f5"
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Parent Business
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 4,
              flexWrap: "wrap"
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Business Name:
              </Typography>
              <CopyableText
                text={businessGroup.BusinessName || ""}
                value={businessGroup.BusinessId || ""}
                isShowtext={true}
                isSplitText={false}
              />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Merchant ID:
              </Typography>
              <Typography variant="body1">
                {businessGroup.MerchantId || "-"}
              </Typography>
              {businessGroup.MerchantId && (
                <CopyToClipboardWrapper
                  text={businessGroup.MerchantId.toString()}
                  cell="Merchant ID"
                />
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {/* Child Business Count Card */}
      {businessGroup?.ChildMerchants &&
        businessGroup?.ChildMerchants?.length > 0 && (
          <ChildBusinessCountCard
            count={businessGroup?.ChildMerchants?.length || 0}
            loading={apiStatus === "fetching"}
          />
        )}

      <CustomTableWithPagination
        columns={columns}
        data={hasValidData ? businessGroup?.ChildMerchants : []}
        apiStatus={apiStatus}
        payloadSize={payloadSize}
        currentPage={currentPage}
        totalCount={businessGroup?.ChildMerchants?.length}
        lastEvaluatedKey={null}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        handleChangePage={handleChangePage}
        isShowPagination={false}
        noDataFoundMessage={constants.noDataFoundMessage}
      />

      {/* Add Business Modal */}
      <Modal
        open={openAddDialog}
        setOpen={setOpenAddDialog}
        className="add_business_modal"
        fullWidth={true}
      >
        <DialogTitle
          sx={{ m: 0, p: 2 }}
          id="customized-dialog-title"
        ></DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => {
            if (!modalLoading) {
              setOpenAddDialog(false);
              setSelectedBusiness([]);
              setErrorsMessage({ type: "", message: "" });
              setBusinessMessages([]);
            }
          }}
          disabled={modalLoading}
          sx={{
            position: "absolute",
            right: 8,
            top: 2,
            color: (theme) =>
              modalLoading ? theme.palette.grey[400] : theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers ref={addDialogContentRef}>
          {errorsMessage?.message && (
            <div style={{ marginTop: "2px", marginBottom: "1px" }}>
              <Alert severity={errorsMessage?.type as any}>
                {errorsMessage?.message}
              </Alert>
            </div>
          )}
          <Typography gutterBottom>{"Add Child Business"}</Typography>
          <Businesssearch
            searchBusinessColumns={searchBusinessColumns}
            accessToken={accessToken}
            apiBaseUrl={apiEnpoint}
            handleRowClick={(params: any) => {
              const isSelected = selectedBusiness.some(
                (b) => b.BusinessId === params.row.BusinessId
              );
              if (isSelected) {
                setSelectedBusiness(
                  selectedBusiness.filter(
                    (b) => b.BusinessId !== params.row.BusinessId
                  )
                );
              } else {
                setSelectedBusiness([...selectedBusiness, params.row]);
              }
            }}
          />
          {businessMessages.length > 0 && (
            <Box
              sx={{
                mt: 2,
                mb: 1,
                display: "flex",
                flexDirection: "column",
                gap: 1
              }}
            >
              {businessMessages.map((msg, index) => (
                <Alert key={index} severity={msg.type}>
                  <strong>{msg.businessName}:</strong> {msg.message}
                </Alert>
              ))}
            </Box>
          )}
          <BoxModal mt={2}>
            <CustomButton
              callBack={handleAddBusiness}
              disabled={modalLoading || selectedBusiness.length === 0}
            >
              {modalLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                `Add${
                  selectedBusiness.length > 0
                    ? ` (${selectedBusiness.length})`
                    : ""
                }`
              )}
            </CustomButton>
            <CustomButton
              callBack={() => {
                if (!modalLoading) {
                  setOpenAddDialog(false);
                  setSelectedBusiness([]);
                  setErrorsMessage({ type: "", message: "" });
                  setBusinessMessages([]);
                }
              }}
              disabled={modalLoading}
            >
              Cancel
            </CustomButton>
          </BoxModal>
        </DialogContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={openDeleteModal} setOpen={setOpenDeleteModal}>
        <DialogTitle
          sx={{ m: 0, p: 2 }}
          id="customized-dialog-title"
        ></DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => {
            setOpenDeleteModal(false);
            setErrorsMessage({ type: "", message: "" });
          }}
          disabled={modalLoading}
          sx={{
            position: "absolute",
            right: 8,
            top: 2,
            color: (theme) =>
              modalLoading ? theme.palette.grey[400] : theme.palette.grey[500]
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers ref={deleteDialogContentRef}>
          {errorsMessage?.message && (
            <div style={{ marginTop: "2px", marginBottom: "1px" }}>
              <Alert severity={errorsMessage?.type as any}>
                {errorsMessage?.message}
              </Alert>
            </div>
          )}
          <Typography gutterBottom>
            {`Are you sure you want to remove ${selectedMerchant?.BusinessName}`}
            from the business group?
          </Typography>

          <BoxModal mt={2}>
            <CustomButton
              callBack={confirmRemoveBusiness}
              disabled={modalLoading}
            >
              {modalLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Remove"
              )}
            </CustomButton>
            <CustomButton
              callBack={() => {
                setOpenDeleteModal(false);
                setErrorsMessage({ type: "", message: "" });
              }}
              disabled={modalLoading}
            >
              Cancel
            </CustomButton>
          </BoxModal>
        </DialogContent>
      </Modal>
    </div>
  );
};

export default BusinessGroupPage;
